// States of Matter: heat a box of particles and watch them lock, slide, or fly.
// Lesson, then do it: three steps per round, free sliding any time.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { makeLoop } from '@kit/loop';
import type { GameModule, GameContext } from '@shell/types';
import { createSim, step, stats, phaseOf, MIN_C, MAX_C, MELT_C, BOIL_C, type Sim } from './sim';

interface Step {
  lesson: string;
  task: string;
  kind: 'state' | 'mark' | 'choice';
  want?: 'solid' | 'liquid' | 'gas';
  markNear?: number;      // mark steps: target temperature
  markTol?: number;
  choices?: string[];
  answer?: number;
  why: string;
  needSqueeze?: boolean;
}

function tierFor(level: number): 1 | 2 | 3 { return level <= 3 ? 1 : level <= 6 ? 2 : 3; }

const STEPS: Record<1 | 2 | 3, Step[]> = {
  1: [
    { lesson: 'Everything is made of tiny particles. When they are cold they lock into neat rows and only jiggle in place. That is a solid.', task: 'Slide the heat down until the particles lock into rows, then tap Check.', kind: 'state', want: 'solid', why: 'Cold particles barely move, so they hold their places like bricks in a wall.' },
    { lesson: 'Warm the particles a little and they break out of their rows. They slide past each other but stay together at the bottom. That is a liquid.', task: 'Make a liquid, then tap Check.', kind: 'state', want: 'liquid', why: 'Liquid particles have enough energy to move around, but not enough to escape each other.' },
    { lesson: 'Heat them a lot and the particles fly apart and fill the whole box. That is a gas.', task: 'Make a gas, then tap Check.', kind: 'state', want: 'gas', why: 'Gas particles move so fast they bounce off the walls and spread everywhere.' },
  ],
  2: [
    { lesson: 'The exact temperature where a solid turns into a liquid is its melting point. For ice it is 0 degrees.', task: 'Slide slowly until the rows just break apart, then tap Mark.', kind: 'mark', markNear: MELT_C, markTol: 8, why: 'The particles break out of their rows right at 0 degrees. That is the melting point of water.' },
    { lesson: 'Heat is energy. Giving particles more heat gives them more energy to move.', task: 'Watch the particles as you heat them. What happens to their speed?', kind: 'choice', choices: ['They speed up', 'They slow down', 'They stay the same', 'They stop'], answer: 0, why: 'More heat means more energy, and more energy means faster particles.' },
    { lesson: 'A liquid turns into a gas at its boiling point. For water that is 100 degrees.', task: 'Find the boiling point and tap Mark.', kind: 'mark', markNear: BOIL_C, markTol: 8, why: 'At 100 degrees the particles fly free. That is the boiling point of water.' },
  ],
  3: [
    { lesson: 'Water freezes at 0 degrees and boils at 100 degrees. Those two numbers set the whole thermometer scale we use.', task: 'Set the thermometer to exactly where ice melts, then tap Mark.', kind: 'mark', markNear: MELT_C, markTol: 5, why: 'Ice melts at 0 degrees. Below that water is solid, above it water is liquid.' },
    { lesson: 'Gas particles push on the walls every time they bounce. We call that push pressure.', task: 'Make a gas, tap Squeeze to halve the box, and watch the wall hits. Do particles hit the walls more or less often?', kind: 'choice', choices: ['More often', 'Less often', 'The same', 'They stop hitting'], answer: 0, why: 'Same particles, smaller box: they reach a wall sooner, so there are more hits. That is why squeezing a gas raises its pressure.', needSqueeze: true },
    { lesson: 'Melting and boiling need heat, but the state can also depend on how hard the particles are squeezed.', task: 'Which state has particles that touch but still trade places?', kind: 'choice', choices: ['Liquid', 'Solid', 'Gas', 'None of them'], answer: 0, why: 'Liquid particles stay in contact but slide past each other. Solids lock, gases fly apart.' },
  ],
};

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.currentLevel();
  const tier = tierFor(level);
  const steps = STEPS[tier];
  let si = 0;
  let tC = 20;
  let attempts = 0, correct = 0;
  let missed = false;
  let marked = false;
  const t0 = performance.now();
  let tStep = performance.now();
  let hitsPerSec = 0, hitAcc = 0, hitT = 0;
  let sim: Sim | null = null;

  const lesson = h('div.card', { style: { borderLeft: '8px solid var(--science)' } }, h('p', null, ''));
  const task = h('p.center', { style: { fontWeight: '800', minHeight: '44px' } });
  const stage = h('div');
  const readout = h('div.row', { style: { justifyContent: 'space-between', fontWeight: '800' } }, h('span', null, ''), h('span', null, ''), h('span.muted.small', null, ''));
  const slider = h('input', { type: 'range', min: String(MIN_C), max: String(MAX_C), step: '1', value: '20', 'aria-label': 'Heat' }) as HTMLInputElement;
  const sliderRow = h('div.row', null, h('span.muted.small', null, 'Cold'), slider, h('span.muted.small', null, 'Hot'));
  const squeezeBtn = h('button.btn.small', { onclick: () => { if (!sim) return; sim.squeeze = sim.squeeze === 1 ? 0.5 : 1; squeezeBtn.textContent = sim.squeeze === 1 ? 'Squeeze' : 'Un-squeeze'; } }, 'Squeeze');
  const actionBtn = h('button.btn.primary', { onclick: act }, 'Check');
  const choices = h('div.grid2');
  const actions = h('div.row', null, squeezeBtn, actionBtn);
  host.append(lesson, task, stage, readout, sliderRow, actions, choices);

  const S = makeSurface(stage, { aspect: 0.72, draw: false, onResize: (s) => { if (!sim) sim = createSim(48, s.w, s.h, ctx.rng); else { const sq = sim.squeeze; sim = createSim(48, s.w, s.h, ctx.rng); sim.squeeze = sq; } } });
  slider.addEventListener('input', () => { tC = Number(slider.value); });

  function showStep() {
    const st = steps[si];
    (lesson.firstElementChild as HTMLElement).textContent = st.lesson;
    task.textContent = `Step ${si + 1} of 3: ${st.task}`;
    clear(choices);
    missed = false; marked = false; tStep = performance.now();
    squeezeBtn.style.display = st.needSqueeze ? '' : 'none';
    if (st.kind === 'choice') {
      actionBtn.style.display = 'none';
      st.choices!.forEach((c, i) => choices.append(h('button.choice', { style: { fontSize: '17px' }, onclick: () => answer(i) }, c)));
    } else {
      actionBtn.style.display = '';
      actionBtn.textContent = st.kind === 'mark' ? 'Mark' : 'Check';
    }
    ctx.speak(st.task);
  }

  function pass(st: Step) {
    if (!missed) { attempts++; correct++; }
    ctx.report({ skill: 'chemistry', level, correct: !missed, hintUsed: missed, ms: performance.now() - tStep, problemId: `som:${tier}:${si}` });
    ctx.sound('ding');
    ctx.say(st.why);
    si++;
    if (si >= steps.length) {
      if (tier === 2) ctx.charm('charm.boil');
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), 1400);
      task.textContent = 'Round complete. Keep sliding and watching if you like.';
      actionBtn.style.display = 'none'; clear(choices);
      return;
    }
    setTimeout(showStep, 1600);
  }

  function fail(st: Step, hint: string) {
    if (!missed) { attempts++; missed = true; ctx.report({ skill: 'chemistry', level, correct: false, hintUsed: false, ms: performance.now() - tStep, problemId: `som:${tier}:${si}` }); }
    ctx.sound('buzz');
    ctx.say(hint);
    void st;
  }

  function act() {
    const st = steps[si];
    if (!sim) return;
    if (st.kind === 'state') {
      const ph = phaseOf(tC);
      if (ph === st.want) pass(st);
      else fail(st, st.want === 'solid' ? 'Colder. Slide left until the rows lock.' : st.want === 'gas' ? 'Hotter. Slide right until they fill the box.' : (ph === 'solid' ? 'A bit warmer, so the rows break but they stay low.' : 'A bit cooler, so they settle to the bottom.'));
    } else if (st.kind === 'mark') {
      if (Math.abs(tC - st.markNear!) <= st.markTol!) { marked = true; pass(st); }
      else fail(st, tC < st.markNear! ? `Not yet. The thermometer reads ${tC}. Warmer.` : `Past it. The thermometer reads ${tC}. Cooler.`);
    }
  }

  function answer(i: number) {
    const st = steps[si];
    if (i === st.answer) pass(st);
    else fail(st, st.needSqueeze ? 'Watch the hits-per-second number while squeezed, then try again.' : 'Try heating and cooling while you watch, then answer again.');
  }

  function update(dt: number) {
    if (!sim) return;
    step(sim, tC, dt, ctx.rng);
    hitAcc += sim.wallHits; hitT += dt;
    if (hitT >= 0.5) { hitsPerSec = Math.round(hitAcc / hitT); hitAcc = 0; hitT = 0; }
  }

  function draw() {
    if (!sim) return;
    const c = S.ctx, W = S.w, H = S.h;
    const ph = phaseOf(tC);
    c.fillStyle = ph === 'solid' ? '#e3f0fb' : ph === 'liquid' ? '#eaf6ea' : '#fdf0e6';
    c.fillRect(0, 0, W, H);
    const right = W * sim.squeeze;
    if (sim.squeeze < 1) { c.fillStyle = '#9a9aa3'; c.fillRect(right, 0, W - right, H); }
    c.fillStyle = ph === 'solid' ? '#4f8fe6' : ph === 'liquid' ? '#3bb3a9' : '#f08a3c';
    for (const q of sim.p) { c.beginPath(); c.arc(q.x, q.y, sim.r, 0, 7); c.fill(); }
    // thermometer
    const tx = W - 18, ty = 12, th = H - 40;
    c.fillStyle = '#fff'; c.fillRect(tx - 6, ty, 12, th);
    const frac = (tC - MIN_C) / (MAX_C - MIN_C);
    c.fillStyle = tC < MELT_C ? '#4f8fe6' : tC < BOIL_C ? '#e2574c' : '#f08a3c';
    c.fillRect(tx - 4, ty + th * (1 - frac), 8, th * frac);
    c.beginPath(); c.arc(tx, ty + th + 8, 10, 0, 7); c.fill();
    c.strokeStyle = '#2a2a2e'; c.lineWidth = 1; c.strokeRect(tx - 6, ty, 12, th);
    // labels
    (readout.children[0] as HTMLElement).textContent = `${tC} °C`;
    (readout.children[1] as HTMLElement).textContent = ph === 'solid' ? 'Solid' : ph === 'liquid' ? 'Liquid' : 'Gas';
    (readout.children[2] as HTMLElement).textContent = ph === 'gas' ? `${hitsPerSec} wall hits/sec` : '';
  }

  const loop = makeLoop(update, draw);
  loop.start();
  showStep();
  return { destroy() { loop.stop(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'science.states-of-matter',
  title: 'States of Matter',
  blurb: 'Heat a box of particles. Watch them lock, slide, and fly.',
  category: 'science',
  skills: ['chemistry'],
  levelRange: [1, 10],
  unlock: { stars: 9 },
  mount,
};
export default mod;
